import { JsonConverter, JsonElementType, JsonObject, JsonProperty } from "ta-json";

import type { MapCultureTo } from "../../base-types";
import { MemberConditionConverter } from "../../converters/member-condition-converter";
import { IMemberCondition, MemberCondition } from "./member-condition";
import { IMemberDefinition, MemberDefinition } from "./member-definition";

/**
 * Member groups combine multiple members for the purpose of logical grouping, representation (API, UI) and/or access level.
 */
export interface IMemberGroup {
    /**
     * Language agnostic case insensitive name of the group.
     * Must be unique per group and {@link IEntityDefinition}.
     */
    name: string;

    /**
     * Language agnostic case insensitive name of the content group.
     * Used for grouping member groups for visual representation.
     */
    contentGroup: string;

    /**
     * Indicates if this member group is owned by the system and cannot be modified or deleted by the regular users.
     */
    isSystemOwned: boolean;

    /**
     * Indicates if this member group has conditions.
     */
    readonly isConditional: boolean;

    /**
     * The conditions that are evaluated to decide if this group should be exposed in the external API/UI.
     * These conditions have no impact on data validity.
     */
    conditions: Array<IMemberCondition>;

    /**
     * Indicates if the read-write access to this member is restricted.
     */
    isSecured: boolean;

    /**
     * Collection the member definitions in this group.
     */
    memberDefinitions: Array<IMemberDefinition>;

    /**
     * Collection of the culture specific member labels.
     */
    labels: MapCultureTo<string>;
}

@JsonObject()
export class MemberGroup implements IMemberGroup {
    @JsonProperty("name")
    name: string;

    @JsonProperty("content_group")
    contentGroup: string;

    @JsonProperty("is_system_owned")
    isSystemOwned: boolean;

    // @JsonProperty("is_conditional")
    get isConditional(): boolean {
        return this.conditions != null && this.conditions.length > 0;
    }

    @JsonProperty("conditions")
    @JsonElementType(MemberCondition)
    @JsonConverter(MemberConditionConverter)
    conditions: Array<IMemberCondition> = [];

    @JsonProperty("is_secured")
    isSecured: boolean;

    @JsonProperty("members")
    @JsonElementType(MemberDefinition)
    memberDefinitions: Array<IMemberDefinition> = [];

    @JsonProperty("labels")
    labels: MapCultureTo<string> = {};

    constructor(init?: Partial<IMemberGroup>) {
        Object.assign(this, init);
    }
}
